import React, { useEffect, useState } from 'react';
import { Col, PopoverBody, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import uuid from 'react-uuid';

import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { DropdownOption } from 'shared/prop-types/ReducerProps';
import { colors } from 'shared/defaultTheme';

import { Button } from '../Buttons';
import { TextField } from '../form/Inputs';
import { Checkbox } from '../form/CheckBox';

import { PopoverWrapper } from './styles';

const FilterMultiSelect = ({
  className,
  trigger,
  placement,
  options,
  target,
  fieldSearch,
  column,
  onSearch,
  type,
}) => {
  const { t } = useTranslation();
  const [currentOptions, setCurrentOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedAll, setSelectedAll] = useState(false);

  useEffect(() => {
    if (options) {
      setCurrentOptions(options);
    }
  }, [options]);

  const onChangeSearchInput = (e) => {
    const value = e ? e.target.value : '';
    const lowerValue = value ? value.toLowerCase() : '';
    setCurrentOptions(options.filter((option) => option.name?.toLowerCase().includes(lowerValue)));
    if (lowerValue.length <= 0) {
      setCurrentOptions(options);
    }
    setSearchValue(value);
  };

  const onSelectAll = () => {
    const newSelectedAll = !selectedAll;
    setSelectedAll(newSelectedAll);
    setSelectedOptions(
      newSelectedAll ? currentOptions.map((currentOption) => currentOption.value) : []
    );
  };

  const onSelect = (value) => {
    const checked = selectedOptions.some((selectedOption) => selectedOption === value);
    if (checked) {
      setSelectedOptions(selectedOptions.filter((selectedOption) => selectedOption !== value));
      return;
    }
    setSelectedOptions([...selectedOptions, value]);
  };

  const onCallSearch = () => {
    onSearch({
      selectedOptions,
      fieldSearch,
      column,
    });
  };

  const onClear = () => {
    setSearchValue('');
    setSelectedAll(false);
    setSelectedOptions([]);
    onSearch({ selectedOptions: [], column });
  };

  const getName = (name) => {
    if (name === 'access') {
      return t('client_management_access');
    }
    if (name === 'sended') {
      return t('client_management_resend_instruction_access');
    }
    return t('client_management_no_has_access');
  };

  return (
    <PopoverWrapper className={className} trigger={trigger} placement={placement} target={target}>
      <PopoverBody className="popover-body">
        <form className="form">
          {type !== 'boolean' && (
            <div className="form__form-group">
              <TextField
                borderRadius={4}
                withButtonClear={false}
                rightIcon={<SearchIcon fill={colors.black} />}
                value={searchValue}
                onChange={onChangeSearchInput}
              />
            </div>
          )}
          <Row className="w-100 remove-margin-horizontal mb-1">
            <Col sm={12} className="remove-padding-horizontal">
              <Checkbox
                id="filterCheckAll"
                label={t('general_all')}
                checked={selectedAll}
                onClick={onSelectAll}
              />
            </Col>
          </Row>
          {currentOptions.map((option) => (
            <React.Fragment key={uuid()}>
              <Row className="w-100 remove-margin-horizontal mb-1">
                <Col sm={12} className="remove-padding-horizontal">
                  {type === 'boolean' ? (
                    <Checkbox
                      id={`opt${option.value}`}
                      label={getName(option.name)}
                      checked={selectedOptions.some(
                        (selectedOption) => selectedOption === option.value
                      )}
                      onClick={() => onSelect(option.value)}
                    />
                  ) : (
                    <Checkbox
                      id={`opt${option.value}`}
                      label={option.name}
                      checked={selectedOptions.some(
                        (selectedOption) => selectedOption === option.value
                      )}
                      onClick={() => onSelect(option.value)}
                    />
                  )}
                </Col>
              </Row>
            </React.Fragment>
          ))}
        </form>
        <Row className="w-100 remove-margin-horizontal">
          <Col sm={6} xs={6} className="remove-padding-horizontal">
            <Button
              className="text-uppercase"
              type="button"
              color="secondary"
              label={t('general_button_clean')}
              onClick={onClear}
            />
          </Col>
          <Col sm={6} xs={6} className="remove-padding-horizontal">
            <Button
              className="text-uppercase"
              type="button"
              color="primary"
              label={t('general_button_filter')}
              onClick={onCallSearch}
            />
          </Col>
        </Row>
      </PopoverBody>
    </PopoverWrapper>
  );
};

FilterMultiSelect.defaultProps = {
  className: '',
  trigger: 'legacy',
  placement: 'bottom',
  options: [],
  type: '',
};

FilterMultiSelect.propTypes = {
  className: PropTypes.string,
  trigger: PropTypes.string,
  placement: PropTypes.string,
  options: PropTypes.arrayOf(DropdownOption),
  target: PropTypes.string.isRequired,
  fieldSearch: PropTypes.string.isRequired,
  column: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  type: PropTypes.string,
};

export default FilterMultiSelect;
